import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { CalendarClock, Loader2 } from "lucide-react";

const PACKAGE_LOCATIONS = [
  ["LOADING_DOCK", "Loading dock"],
  ["FRONT", "Front"],
  ["REAR", "Rear"],
  ["SIDE", "Side"],
  ["RECEIVING", "Receiving"],
];

export default function FedExReschedulePickupDialog({ open, onOpenChange, quote, onRebooked }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!(open && quote)) return;
    const b = quote.booking || {};
    setForm({
      pickup_date: "",
      ready_time: b.ready_time || "12:00",
      close_time: b.close_time || "15:00",
      package_location: "LOADING_DOCK",
      notify_email: quote.dest_contact_email || quote.customer_email || "",
    });
  }, [open, quote]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.pickup_date) return toast.error("Pickup date is required to rebook the pickup.");
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/fedex-reschedule-pickup`,
        { ...form, schedule_pickup: true });
      toast.success(data.message || `Pickup rebooked — confirmation ${data.pickup_confirmation}.`);
      onOpenChange(false);
      onRebooked?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!quote) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="fedex-reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <CalendarClock className="h-5 w-5 text-[#4D148C]" /> Rebook FedEx Pickup
          </DialogTitle>
          <DialogDescription>
            Re-dispatch a FedEx Freight pickup for the existing shipment (PRO{" "}
            <span className="font-semibold">{quote.fedex_pro_number}</span>). This reuses the same
            BOL &mdash; it does not create a new shipment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="label-caps text-[10px]">Pickup Date <span className="text-[hsl(var(--destructive))]">*</span></Label>
            <Input type="date" data-testid="reschedule-pickup-date" value={form.pickup_date || ""}
              onChange={(e) => set("pickup_date", e.target.value)} className="mt-1 h-9" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-caps text-[10px]">Ready Time</Label>
              <Input type="time" data-testid="reschedule-ready-time" value={form.ready_time || ""}
                onChange={(e) => set("ready_time", e.target.value)} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="label-caps text-[10px]">Dock Close</Label>
              <Input type="time" data-testid="reschedule-close-time" value={form.close_time || ""}
                onChange={(e) => set("close_time", e.target.value)} className="mt-1 h-9" />
            </div>
          </div>
          <div>
            <Label className="label-caps text-[10px]">Package Location</Label>
            <Select value={form.package_location} onValueChange={(v) => set("package_location", v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="reschedule-package-location"><SelectValue /></SelectTrigger>
              <SelectContent>{PACKAGE_LOCATIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps text-[10px]">Pickup Notification Email (optional)</Label>
            <Input data-testid="reschedule-notify-email" value={form.notify_email || ""}
              onChange={(e) => set("notify_email", e.target.value)} className="mt-1 h-9" placeholder="dock@customer.com" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="reschedule-submit" onClick={submit} disabled={busy}
            className="gap-1.5 bg-[#4D148C] text-white hover:bg-[#4D148C]/90">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Rebook Pickup
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
